import { useReactiveVar } from '@apollo/client/react';
import { confirmResetPassword, confirmSignIn, signIn, signOut } from '@aws-amplify/auth';
import { Button, Chip, Paper, TextField, Typography, useMediaQuery } from '@mui/material';
import useEnqueueSnackbar from 'hooks/useEnqueueSnackbar';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useEffect, useState } from 'react';
import PasswordChecklist from 'react-password-checklist';
import { useNavigate } from 'react-router';
import { authVar } from 'state/vars';
import Background from 'ui/atoms/Background/Background';
import Icon from 'ui/atoms/Icon/Icon';
import ComponentWrapper from 'ui/templates/ComponentWrapper/ComponentWrapper';
import { parseCognitoUrlParams } from 'utils/ParseCognitoUrlParams/parseCognitoUrlParams';

type PageVariant = 'RESET' | 'SET';

export interface Props {
  pageVariant: PageVariant;
}

const knownKeys = ['code', 'email'];

const ResetOrSetPassword: React.FC<React.PropsWithChildren<Props>> = ({ pageVariant }) => {
  const { user } = useReactiveVar(authVar);
  const { translation } = useTranslation();
  const params = parseCognitoUrlParams(window.location.href, knownKeys);
  const { code, email } = params;
  const enqueueSnackbar = useEnqueueSnackbar();
  const handleFetchError = (errorMessage: string) => enqueueSnackbar(errorMessage, { snackbartype: 'error' });
  const navigate = useNavigate();
  const [password, setPassword] = useState<string>('');
  const [rePassword, setRePassword] = useState<string>('');
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [countdown, setCountdown] = useState<number>();

  useEffect(() => {
    if (pageVariant === 'SET' && user !== null) navigate('/');
  }, [user]);

  const handleResetAndLogin = async () => {
    if (isPasswordValid && code && email) {
      try {
        if (pageVariant === 'RESET') {
          await confirmResetPassword({
            username: email,
            confirmationCode: code,
            newPassword: password
          });
        } else {
          const resultSignIn = await signIn({ username: email, password: code });
          if (resultSignIn.nextStep) {
            const resultConfirmSignIn = await confirmSignIn({
              challengeResponse: password
            });
            if (resultConfirmSignIn) signOut();
          }
        }
        setCountdown(15);
      } catch (err) {
        console.log(err);
        handleFetchError('' + err);
      }
    }
  };

  const isExtraSmallScreen = useMediaQuery('(min-width: 0px) and (max-width:627px)');
  const isSmallScreen = useMediaQuery('(min-width: 628px) and (max-width:643px)');
  const isMediumScreen = useMediaQuery('(min-width: 644px) and (max-width:663px)');

  const getPaperPadding = () => {
    if (isExtraSmallScreen || isSmallScreen) return '48px 24px';
    if (isMediumScreen) return '48px 40px';
    return '48px 80px';
  };

  const getPaperWidth = () => {
    if (isExtraSmallScreen) {
      return 'calc(100vw - 80px)';
    }
    if (isSmallScreen) {
      return '520px';
    }
    if (isMediumScreen) {
      return '552px';
    }
    return '632px';
  };

  useEffect(() => {
    let intervalId = null;
    if (countdown !== undefined && countdown > 0) {
      intervalId = setInterval(() => {
        setCountdown((prevCountdown) => prevCountdown! - 1);
      }, 1000);
    }

    if (countdown === 0) navigate('/');

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [countdown]);

  return (
    <ComponentWrapper width="100vw" height="100vh" alignItems="center" justifyContent="center">
      <Background id="background" />
      <Paper
        elevation={3}
        data-testid="login-window"
        id="login-window"
        style={{ padding: getPaperPadding(), width: getPaperWidth(), borderRadius: '10px' }}
      >
        {countdown !== undefined ? (
          <ComponentWrapper flexDirection="column" gap="40px">
            <ComponentWrapper flexDirection="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h4">ATLAS</Typography>
              <Icon name="TKHSecurityLogo" width={106} height={40} viewBox="0 0 170 64" />
            </ComponentWrapper>
            <ComponentWrapper flexDirection="column">
              <Typography variant="h5">
                {pageVariant === 'RESET'
                  ? translation.password_reset_successful
                  : translation.account_created_successfully}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {translation.redirect_to_login_hint}
              </Typography>
              <Typography marginTop="24px" variant="h5" alignSelf="center">
                {countdown}
              </Typography>
            </ComponentWrapper>
            <Button
              onClick={() => navigate('/')}
              size="large"
              color="primary"
              variant="contained"
              style={{ color: 'rgba(54, 54, 54, 1)' }}
            >
              {translation.login}
            </Button>
          </ComponentWrapper>
        ) : (
          <ComponentWrapper flexDirection="column" gap="40px">
            <ComponentWrapper flexDirection="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h4">ATLAS</Typography>
              <Icon name="TKHSecurityLogo" width={106} height={40} viewBox="0 0 170 64" />
            </ComponentWrapper>
            <ComponentWrapper flexDirection="column" gap="24px">
              <div>
                <Chip size="medium" variant="filled" label={email} />
              </div>
              <form>
                <ComponentWrapper flexDirection="column" gap="16px">
                  <TextField
                    type="password"
                    label={translation.password}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    id="new-password"
                  />
                  <TextField
                    type="password"
                    label={translation.re_enter_password}
                    value={rePassword}
                    onChange={(event) => setRePassword(event.target.value)}
                    id="repeat-password"
                  />
                </ComponentWrapper>
              </form>
              <Typography variant="body2">{translation.password_requirements}</Typography>
              <ComponentWrapper flexDirection="column" padding="0 8px" gap="2px">
                <PasswordChecklist
                  rules={['minLength', 'specialChar', 'number', 'capital', 'lowercase', 'match']}
                  minLength={8}
                  value={password}
                  valueAgain={rePassword}
                  onChange={(isValid) => {
                    setIsPasswordValid(isValid);
                  }}
                  messages={{
                    minLength: translation.at_least_8_characters,
                    specialChar: translation.a_special_character,
                    number: translation.a_number,
                    capital: translation.a_capital_letter,
                    lowercase: translation.a_lowercase_letter,
                    match: translation.passwords_match
                  }}
                />
              </ComponentWrapper>
              <Button variant="contained" color="primary" onClick={handleResetAndLogin} disabled={!isPasswordValid}>
                {pageVariant === 'RESET' ? translation.complete_reset : translation.create_account}
              </Button>
            </ComponentWrapper>
          </ComponentWrapper>
        )}
      </Paper>
    </ComponentWrapper>
  );
};

export default ResetOrSetPassword;
